import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { FeatherDownload } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Loader } from "../../../../ui/components/Loader";
import { TextField } from "../../../../ui/components/TextField";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { DateRangeFilter } from "../../../../shared/presentation/components/DateRangeFilter";
import {
  DateRangeValue,
  formatDateForApi,
} from "../../../../shared/presentation/utils/dateRangeFilters";
import { usePersistedDateRange } from "../../../../shared/presentation/hooks/usePersistedDateRange";
import { useMinimumLoading } from "../../../../shared/presentation/hooks/useMinimumLoading";
import { usePersistedSort } from "../../../../shared/presentation/hooks/usePersistedSort";
import { usePersistedState } from "../../../../shared/presentation/hooks/usePersistedState";
import {
  EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS,
  addDaysLocal,
  parseYmdToLocalDate,
  toYmd,
  validateExportDateRange,
} from "../../../../shared/utils/date";
import { InvoiceEntity, InvoiceListStatus } from "../../domain";
import { useInvoiceCounts } from "../hooks/useInvoiceCounts";
import { useInvoiceExcelExport } from "../hooks/useInvoiceExcelExport";
import { useInvoiceList } from "../hooks/useInvoiceList";
import { InvoiceListToolbar } from "./InvoiceListToolbar";
import { InvoiceStatsCards } from "./InvoiceStatsCards";
import { InvoiceTable } from "./InvoiceTable";
import { FeatherX } from "@subframe/core";

const ITEMS_PER_PAGE = 10;
const INVOICE_LIST_STATUSES: InvoiceListStatus[] = [
  "all",
  "pending",
  "on_hold",
  "under_review",
  "posted",
  "duplicate",
  "rejected",
];
type ListLoadingReason = "initial" | "pagination" | "refresh" | null;

const deserializeInvoiceStatus = (raw: string, fallback: InvoiceListStatus) => {
  const parsed = JSON.parse(raw);
  return typeof parsed === "string" &&
    INVOICE_LIST_STATUSES.includes(parsed as InvoiceListStatus)
    ? (parsed as InvoiceListStatus)
    : fallback;
};

const InvoicesSection: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = usePersistedState<InvoiceListStatus>(
    "invoices.list.status",
    "all",
    { deserialize: deserializeInvoiceStatus },
  );
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = usePersistedDateRange(
    "invoices.list.dateRange",
  );
  const [sort, setSort] = usePersistedSort("invoices.list", {
    by: "created_at",
    order: "desc",
    label: "Created Date: Newest First",
  });
  const [listLoadingReason, setListLoadingReason] =
    useState<ListLoadingReason>("initial");
  const listLoadingStartedRef = useRef(false);

  const { invoices, pagination, loading, error, fetchInvoices } =
    useInvoiceList();
  const countParams = useMemo(
    () => ({
      date_from:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      date_to:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
    }),
    [dateRange.from, dateRange.preset, dateRange.to],
  );
  const {
    counts: invoiceCounts,
    loading: countsLoading,
    fetchCounts,
  } = useInvoiceCounts(countParams);

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState<string>("");
  const [exportToDate, setExportToDate] = useState<string>("");
  const [exportError, setExportError] = useState<string>("");
  const { download: downloadExport, loading: isExporting } =
    useInvoiceExcelExport();

  const listParams = useMemo(
    () => ({
      page,
      limit: ITEMS_PER_PAGE,
      status,
      search: searchTerm,
      dateFrom:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      dateTo:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
      sortBy: sort.by as
        | "created_at"
        | "updated_at"
        | "filename"
        | "status"
        | "original_filename",
      sortOrder: sort.order as "asc" | "desc",
    }),
    [
      dateRange.from,
      dateRange.preset,
      dateRange.to,
      page,
      searchTerm,
      sort.by,
      sort.order,
      status,
    ],
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(
      () => {
        fetchInvoices(listParams);
      },
      searchTerm.trim() ? 500 : 0,
    );

    return () => window.clearTimeout(timeoutId);
  }, [fetchInvoices, listParams, searchTerm]);

  useEffect(() => {
    if (loading) {
      listLoadingStartedRef.current = true;
      return;
    }

    if (listLoadingStartedRef.current) {
      listLoadingStartedRef.current = false;
      setListLoadingReason(null);
    }
  }, [loading]);

  const resetExportState = () => {
    setExportFromDate("");
    setExportToDate("");
    setExportError("");
  };

  const exportDateRangeError = validateExportDateRange(
    exportFromDate,
    exportToDate,
  );

  const handleStatusChange = (nextStatus: InvoiceListStatus) => {
    if (nextStatus === status) return;

    setListLoadingReason("refresh");
    setStatus(nextStatus);
    setPage(1);
  };

  const handleDateRangeChange = (nextDateRange: DateRangeValue) => {
    if (
      nextDateRange.preset === dateRange.preset &&
      nextDateRange.from?.getTime() === dateRange.from?.getTime() &&
      nextDateRange.to?.getTime() === dateRange.to?.getTime()
    ) {
      return;
    }

    setListLoadingReason("refresh");
    setDateRange(nextDateRange);
    setPage(1);
  };

  const handleSearchTermChange = (value: string) => {
    if (value === searchTerm) return;

    setListLoadingReason("refresh");
    setSearchTerm(value);
    setPage(1);
  };

  const handleSortChange = (
    sortBy: string,
    sortOrder: string,
    label: string,
  ) => {
    if (
      sortBy === sort.by &&
      sortOrder === sort.order &&
      label === sort.label
    ) {
      return;
    }

    setListLoadingReason("refresh");
    setSort({ by: sortBy, order: sortOrder, label });
    setPage(1);
  };

  const handleRefresh = useCallback(() => {
    setListLoadingReason("refresh");
    fetchInvoices(listParams);
    fetchCounts(countParams);
  }, [countParams, fetchCounts, fetchInvoices, listParams]);

  const handlePageChange = (nextPage: number) => {
    if (nextPage === page) return;

    setListLoadingReason("pagination");
    setPage(nextPage);
  };

  const handleReviewClick = (document: InvoiceEntity) => {
    navigate(`/invoices/review`, {
      state: {
        invoiceJson: document.invoice_json,
        fileName: document.original_filename,
        documentId: document.id.toString(),
        invoiceDocument: document,
        signedUrl: document.signed_url,
        isCustomerMatched:
          document.invoice_json.customer_id !== undefined &&
          document.invoice_json.customer_id !== "",
        isIdMatched: document.invoice_json.invoice_number !== "",
        isEdit: document.status === "1",
        jobId: document.jobid,
      },
    });
  };

  const handleExportFromDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const v = e.target.value;
    setExportFromDate(v);
    setExportError("");
    if (!v) return;
    const fromD = parseYmdToLocalDate(v);
    if (!fromD) return;
    if (exportToDate) {
      const toD = parseYmdToLocalDate(exportToDate);
      const maxTo = addDaysLocal(
        fromD,
        EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1,
      );
      if (toD && toD > maxTo) {
        setExportToDate(toYmd(maxTo));
      } else if (toD && toD < fromD) {
        setExportToDate(v);
      }
    }
  };

  const handleExportToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setExportToDate(v);
    setExportError("");
    if (!v) return;
    const toD = parseYmdToLocalDate(v);
    if (!toD) return;
    if (exportFromDate) {
      const fromD = parseYmdToLocalDate(exportFromDate);
      const minFrom = addDaysLocal(
        toD,
        -(EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1),
      );
      if (fromD && fromD < minFrom) {
        setExportFromDate(toYmd(minFrom));
      } else if (fromD && fromD > toD) {
        setExportFromDate(v);
      }
    }
  };

  const exportFromMin =
    exportToDate.trim() && parseYmdToLocalDate(exportToDate.trim())
      ? toYmd(
          addDaysLocal(
            parseYmdToLocalDate(exportToDate.trim())!,
            -(EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1),
          ),
        )
      : undefined;
  const exportFromMax =
    exportToDate.trim() && parseYmdToLocalDate(exportToDate.trim())
      ? exportToDate.trim()
      : undefined;
  const exportToMin =
    exportFromDate.trim() && parseYmdToLocalDate(exportFromDate.trim())
      ? exportFromDate.trim()
      : undefined;
  const exportToMax =
    exportFromDate.trim() && parseYmdToLocalDate(exportFromDate.trim())
      ? toYmd(
          addDaysLocal(
            parseYmdToLocalDate(exportFromDate.trim())!,
            EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1,
          ),
        )
      : undefined;

  const handleExportDownload = async () => {
    setExportError("");
    const rangeErr = validateExportDateRange(exportFromDate, exportToDate);
    if (rangeErr) {
      setExportError(rangeErr);
      return;
    }

    try {
      await downloadExport({
        fromDate: exportFromDate.trim(),
        toDate: exportToDate.trim(),
      });
      setIsExportModalOpen(false);
      resetExportState();
    } catch (e: any) {
      setExportError(e?.message || "Failed to download invoice export.");
    }
  };

  const showTableSkeleton =
    (listLoadingReason === "initial" && !pagination) ||
    listLoadingReason === "pagination" ||
    (loading && !pagination);
  const showInlineLoading =
    !showTableSkeleton && (listLoadingReason === "refresh" || loading);
  const smoothTableSkeleton = useMinimumLoading(showTableSkeleton);
  const smoothInlineLoading = useMinimumLoading(showInlineLoading);

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 pt-8 pb-40">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Invoices
          </span>
          <span className="text-body font-body text-subtext-color">
            Track and manage your invoice workflow
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="neutral-tertiary"
            icon={<FeatherDownload />}
            onClick={() => setIsExportModalOpen(true)}
          >
            Export
          </Button>
          <Button onClick={() => navigate("/invoices/all")}>
            View All Invoices
          </Button>
        </div>
      </div>

      <DateRangeFilter value={dateRange} onChange={handleDateRangeChange} />

      <InvoiceStatsCards
        counts={invoiceCounts}
        loading={countsLoading}
        onSelectStatus={handleStatusChange}
      />

      <InvoiceListToolbar
        status={status}
        onStatusChange={handleStatusChange}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
        sort={sort}
        onSortChange={handleSortChange}
        onRefresh={handleRefresh}
        loading={loading}
      />

      <InvoiceTable
        invoices={invoices}
        loading={smoothTableSkeleton}
        inlineLoading={smoothInlineLoading}
        error={error}
        pagination={pagination}
        itemsPerPage={ITEMS_PER_PAGE}
        searchTerm={searchTerm}
        onReview={handleReviewClick}
        onPageChange={handlePageChange}
      />

      <DialogLayout
        open={isExportModalOpen}
        onOpenChange={(open) => {
          setIsExportModalOpen(open);
          if (!open) resetExportState();
        }}
      >
        <div className="flex w-144 flex-col items-start gap-6 bg-white px-8 py-8">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Export invoices to Excel
            </span>
            <IconButton
              icon={<FeatherX />}
              onClick={() => setIsExportModalOpen(false)}
              disabled={isExporting}
            />
          </div>

          <div className="flex w-full flex-col items-start gap-4">
            {exportError && (
              <div className="w-full rounded-md border border-error-200 bg-error-50 px-4 py-3">
                <span className="text-body font-body text-error-700">
                  {exportError}
                </span>
              </div>
            )}

            <div className="flex w-full items-start gap-4">
              <TextField
                className="h-auto w-full"
                variant="filled"
                label="From date"
                helpText="Required"
              >
                <TextField.Input
                  type="date"
                  required
                  value={exportFromDate}
                  onChange={handleExportFromDateChange}
                  disabled={isExporting}
                  min={exportFromMin}
                  max={exportFromMax}
                />
              </TextField>
              <TextField
                className="h-auto w-full"
                variant="filled"
                label="To date"
                helpText="Required"
              >
                <TextField.Input
                  type="date"
                  required
                  value={exportToDate}
                  onChange={handleExportToDateChange}
                  disabled={isExporting}
                  min={exportToMin}
                  max={exportToMax}
                />
              </TextField>
            </div>

            <div className="w-full rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
              <span className="text-body font-body text-subtext-color">
                Select a date range of up to{" "}
                {EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS} days (inclusive). Both
                dates are required.
              </span>
            </div>
          </div>

          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="neutral-tertiary"
              onClick={() => setIsExportModalOpen(false)}
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExportDownload}
              disabled={isExporting || !!exportDateRangeError}
            >
              {isExporting ? (
                <div className="flex items-center gap-2">
                  <Loader size="small" />
                  <span>Generating...</span>
                </div>
              ) : (
                "Download"
              )}
            </Button>
          </div>
        </div>
      </DialogLayout>
    </div>
  );
};

export default InvoicesSection;
